
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { useEffect } from 'react';

export default function ExteriorPaintingPage() {
  useEffect(() => {
    // Update page title and meta tags
    document.title = 'Exterior Painting Cape Town | Weather-Resistant House Painting | Professional Contractors';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional exterior painting services in Cape Town. Weather-resistant house painting, pressure washing, surface preparation. Protect and beautify your property. Free quotes available.');
    }

    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'exterior painting Cape Town, house painting, weather resistant paint, exterior contractors, property painting');
    }

    // Update canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/services/exterior-painting`);
    }

    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Exterior Painting Services Cape Town",
      "description": "Professional exterior painting services for residential and commercial properties in Cape Town including weather-resistant coatings and surface preparation.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/services/exterior-painting`,
      "provider": {
        "@type": "LocalBusiness",
        "name": "Cape Town Professional Painters",
        "areaServed": "Cape Town, Western Cape, South Africa"
      }
    });
    document.head.appendChild(script);

    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('Exterior Painting Services Cape Town')) {
        existingScript.remove();
      }
    };
  }, []);

  const exteriorServices = [
    {
      title: 'Complete House Exteriors',
      description: 'Full exterior painting including walls, trim, doors, and architectural details.',
      features: ['Wall painting', 'Trim & fascia', 'Door refinishing', 'Window frames']
    },
    {
      title: 'Deck & Fence Painting',
      description: 'Specialized treatments for outdoor wood structures and metal fencing.',
      features: ['Wood staining', 'Metal coating', 'Weather protection', 'Color matching']
    },
    {
      title: 'Pressure Washing & Prep',
      description: 'Thorough cleaning and surface preparation for optimal paint adhesion.',
      features: ['High-pressure cleaning', 'Mold removal', 'Surface repairs', 'Primer application']
    },
    {
      title: 'Protective Coatings',
      description: 'Advanced coatings for extreme weather protection and longevity.',
      features: ['UV protection', 'Salt air resistance', 'Moisture barriers', 'Anti-fade technology']
    }
  ];

  const weatherChallenges = [
    {
      challenge: 'Coastal Salt Air',
      solution: 'Marine-grade paints with enhanced salt resistance and anti-corrosion properties.',
      icon: 'ri-water-percent-line'
    },
    {
      challenge: 'UV Radiation',
      solution: 'UV-resistant formulations that prevent fading and chalking in intense sunlight.',
      icon: 'ri-sun-line'
    },
    {
      challenge: 'Wind & Rain',
      solution: 'Flexible, waterproof coatings that expand and contract with weather changes.',
      icon: 'ri-windy-line'
    },
    {
      challenge: 'Temperature Fluctuations',
      solution: "Thermal-stable paints designed for Cape Town's variable climate conditions.",
      icon: 'ri-temp-cold-line'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 to-black text-white py-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/24556b097b847a1468041d2c0b3fc26f.jpg"
            alt="Professional exterior painting services in Cape Town"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-black/70"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Professional <span className="text-yellow-400">Exterior Painting</span> Services
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Protect and beautify your property with weather-resistant exterior painting services in Cape Town. Our expert contractors use premium materials designed for coastal conditions.
            </p>
            <div className="flex gap-4">
              <Link
                to="/contact"
                className="bg-yellow-400 text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-yellow-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Quote
              </Link>
              <Link
                to="/services"
                className="bg-gray-100 text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Exterior Services */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
              Comprehensive Exterior Painting Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              From complete house makeovers to targeted repairs, our network of exterior painting specialists delivers lasting protection and enhanced curb appeal.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {exteriorServices.map((service, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-shadow border border-gray-100">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                
                <div className="grid grid-cols-2 gap-3">
                  {service.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center">
                      <i className={`${feature.icon} text-black text-2xl`}></i>
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Weather Challenges */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Cape Town Weather Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our contractors use specialized paints and techniques designed specifically for Cape Town's unique coastal climate
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {weatherChallenges.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-shadow border border-gray-100">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${item.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-lg font-bold text-black mb-3">{item.challenge}</h3>
                <p className="text-gray-600 text-sm">{item.solution}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Our Exterior Painting Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Systematic approach ensuring long-lasting protection and beautiful results for your property
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
            {[
              {
                step: '01',
                title: 'Property Assessment',
                description: 'Detailed inspection of surfaces, existing paint condition, and weather damage.'
              },
              {
                step: '02',
                title: 'Pressure Washing',
                description: 'Thorough cleaning to remove dirt, mold, and loose paint for optimal adhesion.'
              },
              {
                step: '03',
                title: 'Surface Preparation',
                description: 'Scraping, sanding, patching, and priming all surfaces for perfect paint application.'
              },
              {
                step: '04',
                title: 'Weather-Resistant Application',
                description: 'Professional painting using premium exterior paints designed for coastal conditions.'
              },
              {
                step: '05',
                title: 'Quality Inspection',
                description: 'Final walkthrough, touch-ups, and cleanup ensuring complete satisfaction.'
              }
            ].map((process, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-yellow-400 text-black rounded-full flex items-center justify-center mx-auto mb-6 text-lg font-bold">
                  {process.step}
                </div>
                <h4 className="text-lg font-bold text-black mb-4">{process.title}</h4>
                <p className="text-gray-600 leading-relaxed text-sm">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Benefits of Professional Exterior Painting
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-shield-check-line text-black text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-black mb-4">Weather Protection</h3>
              <p className="text-gray-600">Advanced coatings protect against UV rays, moisture, and coastal salt air damage.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-home-heart-line text-black text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-black mb-4">Increased Property Value</h3>
              <p className="text-gray-600">Fresh exterior paint significantly boosts curb appeal and property market value.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-time-line text-black text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-black mb-4">Long-Term Savings</h3>
              <p className="text-gray-600">Quality exterior painting prevents costly repairs and extends building lifespan.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Related Painting Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Complete your property transformation with our comprehensive painting solutions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Link
              to="/services/interior-painting"
              className="bg-gray-50 rounded-xl p-6 text-center hover:shadow-lg transition-all cursor-pointer group border border-gray-100"
            >
              <i className="ri-home-4-line text-3xl text-yellow-600 mb-4 group-hover:scale-110 transition-transform"></i>
              <h3 className="font-bold text-black group-hover:text-yellow-600 transition-colors">
                Interior Painting
              </h3>
              <p className="text-gray-600 text-sm mt-2">Transform your indoor spaces</p>
            </Link>

            <Link
              to="/services/roof-painting"
              className="bg-gray-50 rounded-xl p-6 text-center hover:shadow-lg transition-all cursor-pointer group border border-gray-100"
            >
              <i className="ri-home-8-line text-3xl text-yellow-600 mb-4 group-hover:scale-110 transition-transform"></i>
              <h3 className="font-bold text-black group-hover:text-yellow-600 transition-colors">
                Roof Painting
              </h3>
              <p className="text-gray-600 text-sm mt-2">Protect and restore your roof</p>
            </Link>

            <Link
              to="/services/commercial-painting"
              className="bg-gray-50 rounded-xl p-6 text-center hover:shadow-lg transition-all cursor-pointer group border border-gray-100"
            >
              <i className="ri-community-line text-3xl text-yellow-600 mb-4 group-hover:scale-110 transition-transform"></i>
              <h3 className="font-bold text-black group-hover:text-yellow-600 transition-colors">
                Commercial Painting
              </h3>
              <p className="text-gray-600 text-sm mt-2">Professional business solutions</p>
            </Link>

            <Link
              to="/services/waterproofing"
              className="bg-gray-50 rounded-xl p-6 text-center hover:shadow-lg transition-all cursor-pointer group border border-gray-100"
            >
              <i className="ri-droplet-line text-3xl text-yellow-600 mb-4 group-hover:scale-110 transition-transform"></i>
              <h3 className="font-bold text-black group-hover:text-yellow-600 transition-colors">
                Waterproofing
              </h3>
              <p className="text-gray-600 text-sm mt-2">Ultimate moisture protection</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 bg-gradient-to-r from-yellow-400 to-yellow-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-8">
            Ready to Enhance Your Property's Exterior?
          </h2>
          <p className="text-xl text-black/80 mb-12 max-w-4xl mx-auto leading-relaxed">
            Connect with Cape Town's most trusted exterior painting contractors through our platform. Get up to 5 quotes and choose the perfect contractor for your project.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-black text-white px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
